import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDangerConfirm } from "../../../shared/context/DangerConfirmContext.jsx";
import { Button } from "../../../shared/components/Button.jsx";
import { Card } from "../../../shared/components/Card.jsx";
import { Badge } from "../../../shared/components/Badge.jsx";
import { CompanyModal } from "./CompanyModal.jsx";
import { formatInvoiceNumber } from "../models/company.js";
import { useCompanies } from "../hooks/useCompanies.js";

/**
 * Billing companies with their invoice numbering: add, edit, make default, bank accounts, delete.
 * Shown inline in Settings → Companies.
 */
export function CompanyList() {
  const navigate = useNavigate();
  const { confirmDanger } = useDangerConfirm();
  const { companies, loading, addCompany, editCompany, removeCompany, setAsDefault } = useCompanies();
  const [editingCompany, setEditingCompany] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleOpenAdd = () => {
    setEditingCompany(null);
    setIsFormOpen(true);
  };

  const handleOpenEdit = (comp) => {
    setEditingCompany(comp);
    setIsFormOpen(true);
  };

  const handleSaveCompany = async (data) => {
    if (editingCompany) await editCompany(editingCompany.id, data);
    else await addCompany(data);
  };

  const handleDelete = async (comp) => {
    if (companies.length <= 1) {
      alert("At least one company must remain registered.");
      return;
    }
    if (await confirmDanger({ title: "Delete company?", message: `Are you sure you want to delete "${comp.name}"?` })) {
      await removeCompany(comp.id);
    }
  };

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", gap: "0.5rem" }}>
        <span style={{ fontSize: "0.85rem", color: "var(--text-muted)" }}>
          {loading ? "Loading…" : `${companies.length} ${companies.length === 1 ? "company" : "companies"}`}
        </span>
        <Button size="sm" variant="primary" onClick={handleOpenAdd}>
          + Add company
        </Button>
      </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        {companies.map((comp) => {
          const nextGstInv = formatInvoiceNumber(comp, false);
          const nextNonGstInv = formatInvoiceNumber(comp, true);

          return (
            <Card key={comp.id} style={{ position: "relative" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem", flexWrap: "wrap" }}>
                <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                    <span style={{ fontWeight: "700", fontSize: "1rem", color: "var(--text-main)" }}>
                      {comp.name}
                    </span>
                    {comp.isDefault && (
                      <Badge variant="success">Default</Badge>
                    )}
                  </div>
                  {comp.tradeName && (
                    <div style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                      Trade: {comp.tradeName}
                    </div>
                  )}
                  <div style={{ fontSize: "0.78rem", color: "var(--text-dim)", marginTop: "2px" }}>
                    GSTIN: {comp.gstin || "Unregistered"} • {comp.city || "Tamil Nadu"}
                  </div>
                  <div style={{ display: "flex", gap: "0.75rem", flexWrap: "wrap", fontSize: "0.76rem", marginTop: "4px" }}>
                    <span style={{ color: "#38bdf8" }}>
                      GST Seq: <strong>{nextGstInv}</strong> (Next: {comp.nextInvoiceNumber || 1})
                    </span>
                    <span style={{ color: "#f59e0b" }}>
                      Non-GST Seq: <strong>{nextNonGstInv}</strong> (Next: {comp.nonGstNextInvoiceNumber || 1})
                    </span>
                  </div>
                </div>

                <div style={{ display: "flex", gap: "0.35rem", flexShrink: 0, marginLeft: "auto" }}>
                  {!comp.isDefault && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                      onClick={() => setAsDefault(comp.id)}
                      title="Set as Default"
                    >
                      Make Default
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                    onClick={() => navigate(`/banking?company=${comp.id}`)}
                    title="Bank accounts & statements"
                    aria-label="Bank accounts and statements"
                  >
                    🏦
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem" }}
                    onClick={() => handleOpenEdit(comp)}
                    title="Edit Company"
                  >
                    ✏️
                  </button>
                  {companies.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      style={{ fontSize: "0.75rem", padding: "0.25rem 0.5rem", color: "var(--danger)" }}
                      onClick={() => handleDelete(comp)}
                      title="Delete Company"
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            </Card>
          );
        })}
      </div>


      <CompanyModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        company={editingCompany}
        onSave={handleSaveCompany}
      />
    </>
  );
}

export default CompanyList;
